import { useEffect, useState, useMemo } from "react";
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Grid,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Stack,
  TablePagination,
} from "@mui/material";
import LockIcon from "@mui/icons-material/Lock";
import VisibilityIcon from "@mui/icons-material/Visibility";
import api from "../../utils/api";
import { useNavigate } from "react-router-dom";

export default function StudentResultsPage() {
  const [loading, setLoading] = useState(true);
  const [results, setResults] = useState([]);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/exam/results/all");
        setResults(data.results || []);
      } catch (err) {
        console.error("Error loading results:", err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  /**
   * ✅ Split results by scale:
   * - SUBJECT_PRACTICE + POST_UTME => 0–100
   * - JAMB => 0–400
   */
  const summaries = useMemo(() => {
    const nonJamb = results.filter(
      (r) => r.exam_type === "SUBJECT_PRACTICE" || r.exam_type === "POST_UTME"
    );

    const jamb = results.filter((r) => r.exam_type === "JAMB");

    const compute100 = (arr) => {
      if (!arr.length) return { count: 0, avg: 0, best: 0 };
      const scores = arr.map((r) => Number(r.score || 0));
      return {
        count: arr.length,
        avg: Math.round(scores.reduce((a, b) => a + b, 0) / scores.length),
        best: Math.max(...scores),
      };
    };

    const compute400 = (arr) => {
      if (!arr.length) return { count: 0, avg: 0, best: 0 };
      const scores = arr.map((r) => Number(r.score || 0));
      return {
        count: arr.length,
        avg: Math.round(scores.reduce((a, b) => a + b, 0) / scores.length),
        best: Math.max(...scores),
      };
    };

    return {
      nonJamb: compute100(nonJamb),
      jamb: compute400(jamb),
    };
  }, [results]);

  const paginatedResults = results.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" mt={5}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        px: { xs: 1.5, sm: 2.5, md: 3 },
        py: { xs: 2, sm: 3 },
        overflowX: "hidden",
      }}
    >
      <Typography
        fontWeight={800}
        gutterBottom
        sx={{ fontSize: { xs: "1.3rem", sm: "1.6rem" } }}
      >
        🧾 My Exam Results
      </Typography>

      {/* =========================
          SUMMARY
         ========================= */}
      <Grid container spacing={2} mb={3}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: { xs: 2, sm: 2.5 }, textAlign: "center" }}>
            <Typography fontWeight={800} gutterBottom>
              ✅ Subject Practice / Post-UTME (0–100)
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <Typography fontWeight={700}>
                  {summaries.nonJamb.count}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Attempts
                </Typography>
              </Grid>

              <Grid item xs={12} sm={4}>
                <Typography fontWeight={700}>
                  {summaries.nonJamb.avg}%
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Average Score
                </Typography>
              </Grid>

              <Grid item xs={12} sm={4}>
                <Typography fontWeight={700}>
                  {summaries.nonJamb.best}%
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Best Performance
                </Typography>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: { xs: 2, sm: 2.5 }, textAlign: "center" }}>
            <Typography fontWeight={800} gutterBottom>
              🏁 JAMB (0–400)
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <Typography fontWeight={700}>
                  {summaries.jamb.count}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Attempts
                </Typography>
              </Grid>

              <Grid item xs={12} sm={4}>
                <Typography fontWeight={700}>
                  {summaries.jamb.avg} / 400
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Average Score
                </Typography>
              </Grid>

              <Grid item xs={12} sm={4}>
                <Typography fontWeight={700}>
                  {summaries.jamb.best} / 400
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Best Performance
                </Typography>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>

      {/* =========================
          TABLE
         ========================= */}
      <Paper sx={{ p: { xs: 1.5, sm: 2 } }}>
        <TableContainer sx={{ overflowX: "auto" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>#</TableCell>
                <TableCell>Subject</TableCell>
                <TableCell>Score</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Submitted</TableCell>
                <TableCell>Review</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {paginatedResults.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    No completed exams yet.
                  </TableCell>
                </TableRow>
              )}

              {paginatedResults.map((r, index) => {
                const hasSubmitted = Boolean(r.submitted_at);
                const submittedAt = hasSubmitted
                  ? new Date(r.submitted_at)
                  : null;

                const expired =
                  submittedAt &&
                  (Date.now() - submittedAt.getTime()) / 36e5 > 24;

                const isJamb = r.exam_type === "JAMB";

                const scoreNum = Number(r.score || 0);
                const scoreLabel = isJamb
                  ? `${scoreNum} / 400`
                  : `${scoreNum}%`;

                const chipColor = (() => {
                  if (isJamb) {
                    if (scoreNum >= 280) return "success";
                    if (scoreNum >= 200) return "warning";
                    return "error";
                  }
                  if (scoreNum >= 70) return "success";
                  if (scoreNum >= 50) return "warning";
                  return "error";
                })();

                return (
                  <TableRow key={`${r.exam_type}-${r.exam_id}`}>
                    <TableCell>
                      {page * rowsPerPage + index + 1}
                    </TableCell>

                    <TableCell>
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        flexWrap="wrap"
                      >
                        <Typography fontWeight={600}>
                          {isJamb ? "JAMB Practice" : r.subject_name}
                        </Typography>

                        {expired && !isJamb && (
                          <Chip
                            size="small"
                            label="Expired"
                            color="error"
                            icon={<LockIcon />}
                          />
                        )}
                      </Stack>
                    </TableCell>

                    <TableCell>
                      <Chip label={scoreLabel} color={chipColor} />
                    </TableCell>

                    <TableCell>
                      <Chip size="small" label="Completed" color="success" />
                    </TableCell>

                    <TableCell>
                      {submittedAt
                        ? submittedAt.toLocaleString()
                        : "—"}
                    </TableCell>

                    <TableCell>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<VisibilityIcon />}
                        sx={{ width: { xs: "100%", sm: "auto" } }}
                        onClick={() => {
                          if (isJamb) {
                            navigate(
                              `/dashboard/jamb-analysis/${r.exam_id}`
                            );
                          } else {
                            navigate(`/dashboard/result/${r.exam_id}`);
                          }
                        }}
                      >
                        View Details
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={results.length}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={(e, p) => setPage(p)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(+e.target.value);
            setPage(0);
          }}
        />
      </Paper>
    </Box>
  );
}
